import RetrainChatbot from "./components/RetrainChatbot";
import { Menu, Transition } from "@headlessui/react";
import { HiOutlineDotsVertical } from "react-icons/hi";
import ChatbotApiClient from "../../../../methods/api/chatbotapiClient";
import loader from "../../../../methods/loader";
import React, { Fragment, useState, useEffect, useRef } from "react";
import moment from "moment";
import Checkbox from "../../../../components/checkbox/checkbox";
import swal from "sweetalert";
import ApiClient from "../../../../methods/api/apiClient";

// ─── Icons ────────────────────────────────────────────────────────────────────

const FileTextIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 shrink-0">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" />
  </svg>
);

const PenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" />
  </svg>
);

const LoaderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
  </svg>
);

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const isSuccess = status === "success";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isSuccess ? "bg-emerald-50 text-emerald-700" : "bg-indigo-50 text-indigo-600"}`}>
      {isSuccess ? <CheckCircleIcon /> : <LoaderIcon />}
      {isSuccess ? "Trained" : "Pending"}
    </span>
  );
};

// ─── Content Preview (truncated with "more" trigger) ─────────────────────────

function ContentPreview({ text, onViewFull }) {
  const words = text?.split(" ") ?? [];
  const isLong = words.length > 12;
  const preview = isLong ? words.slice(0, 12).join(" ") + "…" : text;

  return (
    <span className="text-gray-500 text-xs leading-relaxed">
      {preview}
      {isLong && (
        <button
          type="button"
          onClick={onViewFull}
          className="ml-1.5 inline-flex items-center gap-1 text-indigo-500 hover:text-indigo-700 font-medium text-xs transition-colors"
        >
          <EyeIcon />
          more
        </button>
      )}
    </span>
  );
}

// ─── Full Content Modal ───────────────────────────────────────────────────────

function ContentModal({ item, onClose }) {
  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
              <FileTextIcon />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-600 font-medium uppercase tracking-wide mb-0.5">Text Block</p>
              <h3 className="text-sm font-semibold text-gray-900 truncate">{item?.subject || "Untitled"}</h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0 ml-3"
          >
            <XIcon />
          </button>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-4 px-6 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-xs text-gray-500">
            Added {moment(item?.created_at).format("MMM D, YYYY")}
          </span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span className="text-xs text-gray-500">
            {item?.content?.split(" ")?.length ?? 0} words
          </span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <StatusBadge status="success" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
            {item?.content || "No content available."}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TextTab = ({ chatbotData }) => {
  const [text, setText] = useState("");
  const [textHeading, setTextHeading] = useState("");
  const [textEdit, setTextEdit] = useState(null);
  const [responselist, setresponselist] = useState([]);
  const [totalChar, setTotalChar] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modalItem, setModalItem] = useState(null);
  const sectionRef = useRef(null);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchData = async () => {
    await ApiClient.get(`sessions/listing?text=true`).then(async res => {
      if (res?.success) setresponselist(res.data);
    });
  };

  useEffect(() => { fetchData(); }, []);

  // ── Submit / Edit ───────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!text.trim()) {
      swal({ text: "Please enter text first.", className: "smallModal", icon: "error" });
      return;
    }
    if (!textHeading?.trim()) {
      swal({ text: "Please enter a subject.", className: "smallModal", icon: "error" });
      return;
    }
    loader(true);
    const payload = { content: text, subject: textHeading };
    const endpoint = textEdit ? `scrap/text/edit` : `scrap/text`;
    if(textEdit){
      payload['id']=textEdit
    }
    const res = await ChatbotApiClient.post(endpoint, payload);
    if (res.success) swal({ text: res.data, className: "smallModal", icon: "success" });
    await fetchData();
    setTextEdit(null);
    setText("");
    setTextHeading("");
    loader(false);
  };

  const handleCancelEdit = () => {
    setTextEdit(null);
    setText("");
    setTextHeading("");
  };

  // ── Selection ───────────────────────────────────────────────────────────────

  const handleSelectAll = () => {
    if (!selectAll) {
      setSelectedIds(responselist.map((item) => item.id));
      setSelectedRows(responselist.map((_, i) => i));
    } else {
      setSelectedIds([]);
      setSelectedRows([]);
    }
    setSelectAll(!selectAll);
  };

  const handleRowSelect = (index, id) => {
    const isSelected = selectedRows.includes(index);
    const nextRows = isSelected ? selectedRows.filter(r => r !== index) : [...selectedRows, index];
    const nextIds = isSelected ? selectedIds.filter(i => i !== id) : [...selectedIds, id];
    setSelectedRows(nextRows);
    setSelectedIds(nextIds);
    setSelectAll(nextRows.length === responselist.length);
  };

  // ── Delete (matching File tab pattern) ──────────────────────────────────────

  const handleDeleteOne = async (id) => {
    const confirm = await swal({ text: "Delete this text block?", icon: "warning", buttons: true, dangerMode: true });
if (!confirm) return;
    loader(true);
    await ApiClient.delete(`sessions/delete?id=${id}`)
      .then(async res => {
        await fetchData();
        swal({ text: 'File deleted successfully.', icon: 'success', className: 'smallModal' });
      })
      .catch(error => { });
    loader(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      swal({ text: "Please select at least one item.", icon: "error", className: "smallModal" });
      return;
    }
    const confirm = await swal({ text: `Delete ${selectedIds.length} selected block${selectedIds.length > 1 ? "s" : ""}?`, icon: "warning", buttons: true, dangerMode: true });
    if (!confirm) return;
    loader(true);
    const res = await ChatbotApiClient.post(`api/delete_multi_text/`, { ids: selectedIds });
    if (res.data) {
      await fetchData();
      setSelectedIds([]);
      setSelectedRows([]);
      setSelectAll(false);
      swal({ text: res.data, icon: "success", className: "smallModal" });
    }
    loader(false);
  };

  const handleEditClick = (item) => {
    setText(item.content);
    setTextHeading(item?.subject);
    setTextEdit(item?.id);
    try {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      sectionRef.current?.focus();
    } catch {}
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="w-full border border-t-0 rounded-b-2xl bg-white p-5 lg:p-8 space-y-6">

      {/* ── Input Card ── */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {textEdit ? "Edit Text Block" : "Add Text Block"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {textEdit
                ? "You are in edit mode — retrain the chatbot after saving."
                : "Add a new text block to train your chatbot."}
            </p>
          </div>
          {textEdit && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
              </svg>
              Editing
            </span>
          )}
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-700">
              Subject <span className="text-red-400">*</span>
            </label>
            <textarea
              ref={sectionRef}
              value={textHeading}
              onChange={(e) => setTextHeading(e.target.value)}
              className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15 outline-none transition-all resize-none placeholder:text-gray-600"
              rows={1}
              placeholder="Enter a subject for this text block…"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-700">
              Content <span className="text-red-400">*</span>
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15 outline-none transition-all placeholder:text-gray-600"
              rows={10}
              placeholder="Paste or type your training content here…"
            />
          </div>

          <div className="flex items-end justify-end gap-2 pt-1">
            {textEdit && (
              <button onClick={handleCancelEdit} type="button" className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors">
                Cancel
              </button>
            )}
            <button onClick={handleSubmit} type="button" className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5">
              {textEdit ? "Save Changes" : "Add Text Block"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Text Blocks List ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Text Blocks</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {responselist.length} block{responselist.length !== 1 ? "s" : ""} added
            </p>
          </div>
          <RetrainChatbot chatbotData={chatbotData} totalChar={totalChar} />
        </div>

        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            {/* Head */}
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {/* <th className="py-3 px-4 text-left w-10">
                  <Checkbox onChange={handleSelectAll} checked={selectAll} />
                </th> */}
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Subject</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Added</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <div className="flex items-center justify-end gap-1">
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>

            {/* Body */}
            <tbody className="divide-y divide-gray-100">
              {responselist.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-14 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                        <FileTextIcon />
                      </div>
                      <p className="text-sm text-gray-600 font-medium">No text blocks added yet</p>
                      <p className="text-xs text-gray-600">Add your first block above to train your chatbot</p>
                    </div>
                  </td>
                </tr>
              ) : (
                responselist.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`transition-colors ${selectedRows.includes(index) ? "bg-indigo-50/50" : "hover:bg-gray-50/60"}`}
                  >
                    {/* Checkbox */}
                    {/* <td className="py-3 px-4">
                      <Checkbox onChange={() => handleRowSelect(index, item.id)} checked={selectedRows.includes(index)} />
                    </td> */}

                    {/* Subject */}
                    <td className="py-3 px-4 max-w-[180px]">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                          <FileTextIcon />
                        </div>
                        <span className="text-gray-800 font-medium text-sm truncate">
                          {item?.subject || item?.text_heading || "Untitled"}
                        </span>
                      </div>
                    </td>

                    {/* Content Preview */}


                    {/* Date */}
                    <td className="py-3 px-4 text-gray-500 text-xs whitespace-nowrap">
                      {moment(item?.created_at).format("MMM D, YYYY")}
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4">
                      <StatusBadge status="success" />
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setModalItem(item)}
                          title="View Content"
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                        >
                          <EyeIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditClick(item)}
                          title="Edit"
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                        >
                          <PenIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteOne(item?.id)}
                          title="Delete"
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 hover:text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Content Modal ── */}
      {modalItem && (
        <ContentModal item={modalItem} onClose={() => setModalItem(null)} />
      )}
    </div>
  );
};

export default TextTab;